import cn from 'classnames';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { useHover } from '../../../../utils';
import { useTableContext } from '../table/table-context';
import { useTableSectionContext } from '../table/table-section-context';

import TableRowContext from './table-row-context';

import type { FocusEvent, ForwardedRef, ReactElement } from 'react';

import type { TableRowProps } from './table-row.types';

import './table-row.scss';

/**
 * The component renders a `<tr>` element and it should contain `TableCell` components.
 * The styles depend on the parent context, which can be `TableHead`, `TableBody`, or `TableFoot`.
 * The component can be disabled or selected.
 *
 * ### Usage
 *
 * ```tsx
 * import { Table, TableHeader, TableBody, TableFoot, TableCell } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomTable() {
 *   return (
 *     <Table>
 *       <TableHeader>
 *         <TableRow />
 *       </TableHeader>
 *       <TableBody>
 *         <TableRow />
 *      </TableBody>
 *      <TableFoot>
 *         <TableRow />
 *      </TableFoot>
 *    </Table>
 *   );
 * }
 * ```
 */
const TableRow = forwardRef(
  (props: TableRowProps, forwardedRef: ForwardedRef<HTMLTableRowElement> | null): ReactElement => {
    const {
      children,
      className,
      disabled = false,
      onBlur,
      onFocus,
      onMouseEnter: onEnter,
      onMouseLeave: onLeave,
      selected = false,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const [focused, setFocused] = useState(false);
    const { handlers, hovered } = useHover({ onEnter, onLeave });
    const timerRef = useRef<number>();

    const tableContext = useTableContext();
    const sectionContext = useTableSectionContext();

    const highlighted = hovered || focused;

    const rowContextValue = useMemo(() => ({ highlighted }), [highlighted]);

    useEffect(() => {
      return () => {
        window.clearTimeout(timerRef.current);
      }; // Clear the timer on unmount.
    }, []);

    const isBodyRow = sectionContext?.variant === 'body';
    const isHeadRow = sectionContext?.variant === 'head';
    const isFootRow = sectionContext?.variant === 'foot';

    const classes = cn(
      'rbui-table-row',
      {
        'rbui-table-row--disabled': disabled,
        'rbui-table-row--highlighted': highlighted,
        'rbui-table-row--selected': selected,
        'rbui-table-row__body': isBodyRow,
        'rbui-table-row__body--large': isBodyRow && tableContext?.rowDensity === 'large',
        'rbui-table-row__body--medium': isBodyRow && tableContext?.rowDensity === 'medium',
        'rbui-table-row__body--small': isBodyRow && tableContext?.rowDensity === 'small',
        'rbui-table-row__foot': isFootRow,
        'rbui-table-row__head': isHeadRow,
      },
      className,
    );

    const handleFocus = (event: FocusEvent<HTMLTableRowElement>): void => {
      window.clearTimeout(timerRef.current);
      setFocused(true);
      onFocus?.(event);
    };
    const handleBlur = (event: FocusEvent<HTMLTableRowElement>): void => {
      // Remove focus from the element with a delay to allow move focus on the next element in the row.
      timerRef.current = window.setTimeout(() => setFocused(false));
      onBlur?.(event);
    };

    return (
      <TableRowContext.Provider value={rowContextValue}>
        <tr
          className={classes}
          onBlur={handleBlur}
          onFocus={handleFocus}
          ref={forwardedRef}
          tabIndex={isBodyRow ? 0 : -1}
          {...handlers}
          {...rest}
        >
          {children}
        </tr>
      </TableRowContext.Provider>
    );
  },
);

TableRow.displayName = 'TableRow';

export default TableRow;
